import { useState, useRef, useCallback } from 'react';
import { Input } from "@/components/ui/input";
import { useMapboxSearch, type AddressSuggestion } from '@/hooks/useMapboxSearch';
import { cn } from '@/lib/utils';

interface AddressLine1AutocompleteProps {
  value: string;
  onChange: (value: string) => void;
  onSuggestionSelect: (address: {
    address_line1: string;
    city: string;
    state: string;
    postal_code: string;
    country: string;
    lat: string | null;
    long: string | null;
  }) => void;
  onKeyDown?: (e: React.KeyboardEvent<HTMLInputElement>) => void;
  placeholder?: string;
  className?: string;
}

export function AddressLine1Autocomplete({
  value,
  onChange,
  onSuggestionSelect,
  onKeyDown,
  placeholder = 'Address Line 1',
  className,
}: AddressLine1AutocompleteProps) {
  const { suggestions, search, clear } = useMapboxSearch();
  const [isOpen, setIsOpen] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const containerRef = useRef<HTMLDivElement>(null);

  const handleInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;
    onChange(val);
    search(val);
    setHighlightedIndex(-1);
    setIsOpen(true);
  }, [onChange, search]);

  const selectSuggestion = useCallback((suggestion: AddressSuggestion) => {
    onChange(suggestion.address_line1);
    onSuggestionSelect({
      address_line1: suggestion.address_line1,
      city: suggestion.city,
      state: suggestion.state,
      postal_code: suggestion.postal_code,
      country: suggestion.country,
      lat: suggestion.lat,
      long: suggestion.long,
    });
    setIsOpen(false);
    setHighlightedIndex(-1);
    clear();
  }, [onChange, onSuggestionSelect, clear]);

  const handleKeyDown = useCallback((e: React.KeyboardEvent<HTMLInputElement>) => {
    if (!isOpen || suggestions.length === 0) {
      // No dropdown open — propagate everything to parent
      onKeyDown?.(e);
      return;
    }

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setHighlightedIndex(prev =>
          prev < suggestions.length - 1 ? prev + 1 : 0
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setHighlightedIndex(prev =>
          prev > 0 ? prev - 1 : suggestions.length - 1
        );
        break;
      case 'Enter':
        if (highlightedIndex >= 0 && highlightedIndex < suggestions.length) {
          e.preventDefault();
          selectSuggestion(suggestions[highlightedIndex]);
        } else {
          // No highlight — let parent handle
          onKeyDown?.(e);
        }
        break;
      case 'Escape':
        setIsOpen(false);
        setHighlightedIndex(-1);
        onKeyDown?.(e);
        break;
      case 'Tab':
        // Let Tab proceed naturally — just close dropdown
        setIsOpen(false);
        setHighlightedIndex(-1);
        break;
      default:
        onKeyDown?.(e);
        break;
    }
  }, [isOpen, suggestions, highlightedIndex, selectSuggestion, onKeyDown]);

  const handleFocus = useCallback(() => {
    if (suggestions.length > 0) {
      setIsOpen(true);
    }
  }, [suggestions.length]);

  const handleBlur = useCallback((e: React.FocusEvent<HTMLInputElement>) => {
    // Check if focus moved to something within our container (i.e. a suggestion item)
    if (containerRef.current?.contains(e.relatedTarget as Node)) {
      return;
    }
    setIsOpen(false);
    setHighlightedIndex(-1);
  }, []);

  return (
    <div ref={containerRef} className="relative">
      <Input
        value={value}
        onChange={handleInputChange}
        onKeyDown={handleKeyDown}
        onFocus={handleFocus}
        onBlur={handleBlur}
        placeholder={placeholder}
        className={cn(className)}
        autoComplete="off"
      />
      {isOpen && suggestions.length > 0 && (
        <div className="absolute left-0 right-0 top-full z-50 mt-1 max-h-[200px] overflow-y-auto rounded-md border bg-popover shadow-md">
          {suggestions.map((suggestion, index) => (
            <div
              key={index}
              className={cn(
                'cursor-pointer px-2 py-1.5 text-xs',
                index === highlightedIndex
                  ? 'bg-accent text-accent-foreground'
                  : 'hover:bg-accent/50'
              )}
              onMouseDown={(e) => {
                e.preventDefault();
                selectSuggestion(suggestion);
              }}
              onMouseEnter={() => setHighlightedIndex(index)}
            >
              {suggestion.formatted}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
